import { useRouter } from 'next/router';

import { FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import { LeaseDuration } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_leaseDuration } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { leaseDurationOptions } from '@zg-rentals/zillow-rental-manager/constants';

import { useScreenSize } from '../../../hooks';
import { setListingField } from '../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import { LEASE_DURATION, LEASE_DURATION_CODE } from './constants';

const inputName = 'leaseDuration';

const LeaseDurationFormFieldSelect = () => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const initialValue = listingDetails?.leaseDuration;

  const options = initialValue ? leaseDurationOptions.slice(1) : leaseDurationOptions;

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_leaseDuration,
    applyToCache: ({ postData }) =>
      postData.leaseDuration ? [setListingField('leaseDuration', LEASE_DURATION_CODE[postData.leaseDuration])] : [],
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_leaseDuration' } },
  });

  const field = useFieldSave({
    fieldName: inputName,
    initialValue,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { listingAlias, leaseDuration: LEASE_DURATION[value as string] as LeaseDuration },
    }),
    autoSaveOnChange: true,
  });

  return (
    <FormField
      data-testid="LeaseDurationFormFieldSelect"
      required
      label={<Label>Lease duration</Label>}
      control={
        <Select
          size={isScreenDesktop ? 'lg' : 'md'}
          name={inputName}
          value={String(field.value ?? '')}
          onChange={(e) => field.onChange(e.target.value)}
          onBlur={field.onBlur}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      }
      error={!!field.error}
      description={<FormHelp>{field.error}</FormHelp>}
    />
  );
};

export default LeaseDurationFormFieldSelect;
